import React, { Component } from 'react';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import FormicaMultiSelectBox from '../custom/FormicaMultiSelectBox';
import { Slider } from 'primereact/slider';
import { AnimatedButton } from '@trlogic/ui-pack';
import { RootState } from '../../redux/store';
import { GraphState } from '../../redux/reducers/GraphReducer';
import { changePageFilter } from '../../redux/actions/PageActions';
import {
  changeGraphFields,
  changeGraphLoading,
  changeInformationCoordinates,
  changeInformationVisible,
  changeLoading,
  changeNodeLimit,
  changeResponse,
  changeSelectedFields,
  changeSelectedNode,
  changeSelectedTransaction,
  changeTransactions,
  changeUpdate,
} from '../../redux/actions/GraphActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { query } from '@trlogic/client-js';
import CaseService from '../../service/CaseService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import LanguageUtil from '../../util/LanguageUtil';
import { CustomFilterCategorical, CustomFilterDateSearch, CustomFilterField, CustomFilterLiteralSearch } from '../../domain/ui/FilterDomains';
import Filter = query.Filter;

class GraphSettingsComponent extends Component<Props> {
  private caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);

  public render() {
    const {
      graph: { selectedTransaction, selectedFields, nodeLimit },
    } = this.props;
    return (
      <div className={'flex items-center justify-between gap-x-32 p-5 border-b'}>
        <div className={'flex items-center gap-x-5 w-full'}>
          <FormicaSearchSelectBox
            value={selectedTransaction}
            rounded
            placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
            onChange={this.onChangeTransaction}
            options={this.getTransactions()}
          />
          <FormicaMultiSelectBox
            rounded
            placeholder={LanguageUtil.get({ id: 'common.label.config', defaultMessage: 'Channel' })}
            selectedItems={selectedFields}
            selectBoxClassName={'max-w-[350px]'}
            onChange={this.onChangeSelectedGraphFieldItems}
            options={this.getGraphFields()}
          />
        </div>
        <div className={'flex items-center justify-end gap-x-5 w-full'}>
          <div className={'w-1/2 flex flex-col gap-y-1'}>
            <Slider value={nodeLimit} min={selectedFields?.length} max={100} onChange={this.onChangeNodeLimit} />
          </div>
          <AnimatedButton
            onClick={this.onClickVisualize}
            customType={'primary'}
            icon={'graph'}
            text={LanguageUtil.get({ id: 'common.label.visualize', defaultMessage: 'Visualize' })}
          />
        </div>
      </div>
    );
  }

  public async componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.graph.caseGraphFields != this.props.graph.caseGraphFields) {
      await this.addFilter();
    }
  }

  private onChangeTransaction = (item: FormicaSelectBoxItem) => {
    this.props.changeSelectedTransaction(item.value);
  };

  private onChangeSelectedGraphFieldItems = (items: FormicaSelectBoxItem[]) => {
    this.props.changeSelectedFields(items);
  };

  private getTransactions = (): FormicaSelectBoxItem[] => {
    const {
      graph: { transactions },
    } = this.props;
    return (
      transactions?.map((transaction) => {
        return { label: transaction.name, value: transaction.name };
      }) || []
    );
  };

  private onChangeNodeLimit = (event: any) => {
    this.props.changeNodeLimit(event.value);
  };

  private getGraphFields = (): FormicaSelectBoxItem[] => {
    const {
      graph: { caseGraphFields },
    } = this.props;
    return (
      caseGraphFields?.graphConfiguration?.map((graphField) => {
        return { label: graphField.label, value: graphField.field, icon: graphField.color, dataType: graphField.dataType };
      }) || []
    );
  };

  private onClickVisualize = async () => {
    const {
      changeResponse,
      changeGraphLoading,
      changeUpdate,
      graph: { update },
    } = this.props;
    const request = this.prepareRequest();
    changeGraphLoading(true);
    try {
      const response = await this.caseService.executeGraph(request);
      changeResponse(response);
      changeGraphLoading(false);
      changeUpdate(update + 1);
    } catch (e) {
      changeGraphLoading(false);
      changeResponse(undefined);
    }
  };

  private addFilter = async () => {
    const {
      changePageFilter,
      pageFilter,
      graph: { caseGraphFields },
    } = this.props;
    if (caseGraphFields && caseGraphFields.graphDefaultFilterRequired) {
      if (caseGraphFields.graphDefaultFilter.startDate && caseGraphFields.graphDefaultFilter.endDate && caseGraphFields.graphDefaultFilter.field) {
        let value: CustomFilterLiteralSearch | CustomFilterCategorical | CustomFilterDateSearch;
        value = {
          startDate: {
            label: caseGraphFields.graphDefaultFilter.startDate.label,
            value: caseGraphFields.graphDefaultFilter.startDate.value,
            type: caseGraphFields.graphDefaultFilter.startDate.type,
          },
          endDate: {
            label: caseGraphFields.graphDefaultFilter.endDate.label,
            value: caseGraphFields.graphDefaultFilter.endDate.value,
            type: caseGraphFields.graphDefaultFilter.endDate.type,
          },
        } as CustomFilterDateSearch;
        const newFilter: CustomFilterField = {
          field: caseGraphFields.graphDefaultFilter.field,
          label: caseGraphFields.graphDefaultFilter.field,
          search: {
            type: 'DATE',
            value: value,
          },
          enabled: true,
        };
        pageFilter.filter.fields.push(newFilter);
        changePageFilter(pageFilter);
      }
    }
  };

  private prepareRequest = (): any => {
    const {
      graph: { selectedTransaction, selectedFields, nodeLimit },
    } = this.props;
    const request = {
      transaction: selectedTransaction,
      defaultFilterKeys: [],
      groups: selectedFields?.map((item) => ({ label: item.label, field: item.value, icon: item.icon, dataType: item.dataType })),
      nodeLimit: nodeLimit,
      filter: this.props.filter,
    };
    return request;
  };
}

const mapStateToProps = (state: RootState) => ({
  pageFilter: state.page.filter,
  graph: state.graph as GraphState,
});
const mapDispatchToProps = {
  changePageFilter,
  changeGraphFields,
  changeTransactions,
  changeGraphLoading,
  changeLoading,
  changeSelectedTransaction,
  changeNodeLimit,
  changeResponse,
  changeSelectedNode,
  changeInformationVisible,
  changeInformationCoordinates,
  changeSelectedFields,
  changeUpdate,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

interface Props extends ReduxProps, FormicaProps {
  filter: Filter;
  onChangeFilter: (filter: Filter) => void;
}

export default withFormica(connector(GraphSettingsComponent));
